import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { localizedPath } from '../../../utils/localizedPath';

/**
 * Footer navigation. Every page of the site is reachable from here in both
 * languages, so nothing is orphaned and a crawler can walk the whole site from
 * any page. Links are built from route keys, so the Spanish footer points at
 * the Spanish slugs.
 *
 * The site now has eight pages. A single flat row of ten items wrapped into an
 * unreadable block, so the pages are grouped under three short headings instead:
 * agents, cases, resources. The email and WhatsApp links that used to sit at the
 * end of this row are not repeated here: the contact row directly above renders
 * both, in both languages, so every link the footer had is still in the footer.
 */
const GROUPS = [
  {
    id: 'agents',
    title: { en: 'Agents', es: 'Agentes' },
    links: [
      { key: 'moodleAgent', en: 'Moodle AI Agent', es: 'Agente de IA para Moodle' },
      { key: 'whatsappAgent', en: 'WhatsApp AI Agent', es: 'Agente de IA para WhatsApp' },
      { key: 'agenticPipelines', en: 'Agentic Pipelines', es: 'Pipelines de Agentes' },
    ],
  },
  {
    id: 'cases',
    title: { en: 'Case studies', es: 'Casos' },
    links: [
      { key: 'caseEstudios', en: 'Estudios Tributarios', es: 'Estudios Tributarios' },
      { key: 'caseProsperia', en: 'Prosperia', es: 'Prosperia' },
    ],
  },
  {
    id: 'resources',
    title: { en: 'Resources', es: 'Recursos' },
    links: [
      { key: 'glossary', en: 'Glossary', es: 'Glosario' },
      { key: 'about', en: 'About', es: 'Nosotros' },
      { key: 'privacy', en: 'Privacy Policy', es: 'Política de Privacidad' },
    ],
  },
];

const labels = {
  en: { nav: 'Footer' },
  es: { nav: 'Pie de página' },
};

const FooterNav = ({
  className,
  ...props
}) => {

  const { lang } = useLanguage();
  const t = labels[lang] || labels.en;

  const classes = classNames(
    'footer-nav',
    'footer-nav-grouped',
    className
  );

  return (
    <nav
      {...props}
      className={classes}
      aria-label={t.nav}
    >
      <div className="footer-nav-groups">
        {GROUPS.map(group => (
          <div className="footer-nav-group" key={group.id}>
            <span className="footer-nav-group-title">
              {group.title[lang] || group.title.en}
            </span>
            <ul className="list-reset">
              {/* Internal links are localized so a Spanish visitor stays on /es. */}
              {group.links.map(link => (
                <li key={link.key}>
                  <Link to={localizedPath(link.key, lang)}>{link[lang] || link.en}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

export default FooterNav;
